import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Hls from "hls.js";
import Features from './Features';
import Navigation from './Navigation';
import { trackEvent } from '../utils/analytics';

// Walkthrough metadata - add new walkthroughs here
export const WALKTHROUGHS = {
  '001': {
    title: 'Chart a Path to a Net Worth Goal',
    description: 'In 5 minutes, learn how this person used Solidly Finance to improve their net worth outlook by $250,000.',
    uploadDate: '2025-10-29',
    src: '/hls/walkthroughs/001/master.m3u8',
    poster: '/hls/walkthroughs/001/solidly-finance-walkthrough-001-poster.jpg',
    fallbackMp4: '/hls/walkthroughs/001/walkthrough-001-720p-fallback.mp4',
    transcript: [
      "Today, we'll cover how you can use Solidly Finance to chart a path to a net worth goal.",
      "This person wants to achieve a net worth of $500,000. On their current trajectory, they are falling short, only reaching $257,000 by the end of 2035. Can they change their financial strategy to get all the way to $500,000? Before we proceed, I'm going to save the current trajectory as a benchmark so we can compare to it later on.",
      "Let's check out their checking account to see what they have going on. They have a monthly post-tax income of $8,000 coming into their checking account. The account itself is currently worth $10,000, and their monthly expenses are $6,000. They plan to transfer a few hundred dollars every year into their investment account.",
      "They've just opened this investment account, which is currently worth $500. You can see the annual contributions hitting the investment account here as well. Returning to their checking account, Solidly Finance is throwing a warning suggesting that they're accumulating a lot of money in a low-growth account. What if we increase the transfer from their checking account to their higher-growth investment account?",
      "By increasing the transfer to their investment account, they maintain a consistent $10,000 balance in their checking account, and the warning message goes away. Now let's step back to the homepage to see how this impacts their trajectory. By increasing their transfer into the investment account to $2,000 per month, this person now reaches a net worth of $352,000 — almost a $100,000 increase from their original plan. On its own, increasing the transfer to their investment account has a huge impact.",
      "But let's see what happens if they could also reduce their expenses. Let's simulate reducing their monthly expenses from $6,000 down to $5,000. We could make this change in a few different places, but since we're already on the homepage, let's make the change here. Reducing their expenses by $1,000 per month increases their achieved net worth by about $120,000 to $474,000. They still aren't quite reaching their net worth goal, but they’re now over $200,000 better off compared to their original plan.",
      "Let's try one last thing. Since they reduced their expenses, their checking account is growing. What if, once they build a bit more of a cash cushion, we transfer the excess savings to their investment account? This cash flow is configured to start in six months, after they’ve built a sufficient safety net in their checking account. You can see the checking account level off around $28,000 — enough to cover roughly six months of expenses at their current rate.",
      "Let's step back up to the homepage one last time and see how this change impacts them long-term. Awesome! You can see here that they are now just reaching their net worth goal of $500,000.",
      "So today we covered how you can use Solidly Finance to chart a path to a net worth goal. The story we followed showed how, by making a few adjustments to this financial plan, they were able to nearly double their achieved net worth from $257,000 to over $500,000 — a $250,000 increase.",
      "You can chart your own path to your financial goals today. Buy Solidly Finance at finance.solidly.ai to get started. Solidly Finance is a one-time purchase, so if you buy today, you own it forever. This has been Alek Hagopian, founder of Solidly Finance. Thanks for listening."
]
  }
  // Add more walkthroughs as they become available:
  // '002': { title: '...', description: '...', src: '...', poster: '...', fallbackMp4: '...', transcript: [...] }
};

export default function WalkthroughVideo() {
  const { id } = useParams();
  const videoRef = useRef(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Get the walkthrough metadata
  const walkthrough = WALKTHROUGHS[id];

  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo(0, 0);

    // Track that user viewed the walkthrough video page
    trackEvent('walkthrough_video_page_view', {
      event_category: 'engagement',
      event_label: `walkthrough_${id}`,
      value: 1
    });
  }, [id]);

  useEffect(() => {
    if (!walkthrough) return;
    
    const video = videoRef.current;
    if (!video || !walkthrough.src) return;

    // If the browser supports HLS natively (Safari, iOS), just set src
    const canPlayNativeHls =
      video.canPlayType("application/vnd.apple.mpegurl") ||
      video.canPlayType("application/x-mpegURL");

    function onVideoError() {
      const mediaErr = video.error;
      if (mediaErr) {
        setError(`Playback error (code ${mediaErr.code}).`);
      } else {
        setError("Playback error.");
      }
    }

    if (canPlayNativeHls) {
      video.src = walkthrough.src;
      video.addEventListener("error", onVideoError);
      return () => video.removeEventListener("error", onVideoError);
    }

    // Otherwise use hls.js for MSE playback (Chrome/Firefox/Edge)
    if (Hls.isSupported()) {
      const hls = new Hls({
        abrEwmaDefaultEstimate: 8_000_000,     // ~8 Mbps initial guess
        abrMaxWithRealBitrate: true,           // use measured bitrate per level
        capLevelToPlayerSize: false,           // don't cap to CSS size
        startLevel: 2                          // 0=360p, 1=720p, 2=1080p  ← force 1080p start
      });
      hls.attachMedia(video);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(walkthrough.src);
      });
      hls.on(Hls.Events.ERROR, (_, data) => {
        console.warn("hls.js error", data);
        if (data.fatal) {
          setError("Playback error. Try reloading or a different network.");
          hls.destroy();
        }
      });

      video.addEventListener("error", onVideoError);

      return () => {
        video.removeEventListener("error", onVideoError);
        hls.destroy();
      };
    }

    // Last resort: fall back to MP4 if provided
    if (walkthrough.fallbackMp4) {
      video.src = walkthrough.fallbackMp4;
    } else {
      setError("Your browser doesn't support HLS playback.");
    }

    video.addEventListener("error", onVideoError);
    return () => video.removeEventListener("error", onVideoError);
  }, [walkthrough]);

  const handleBackToDemoVideo = () => {
    trackEvent('walkthrough_back_to_demo', {
      event_category: 'navigation',
      event_label: `walkthrough_${id}`,
      value: 1
    });
    navigate('/demo');
  };

  // Handle invalid walkthrough ID
  if (!walkthrough) {
    return (
      <div className="demo-video-page">
        <div className="demo-video-container">
          <div className="demo-video-header">
            <h1>Walkthrough Not Found</h1>
            <p className="demo-video-subtitle">
              The walkthrough you're looking for doesn't exist.
            </p>
          </div>
          <div className="demo-video-actions">
            <button 
              className="btn btn-secondary"
              onClick={handleBackToDemoVideo}
            >
              ← Back to Demo Video
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Create VideoObject structured data for SEO
  const videoSchema = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: walkthrough.title,
    description: walkthrough.description,
    thumbnailUrl: `https://finance.solidly.ai${walkthrough.poster}`,
    uploadDate: walkthrough.uploadDate,
    contentUrl: `https://finance.solidly.ai${walkthrough.fallbackMp4}`,
    embedUrl: `https://finance.solidly.ai/walkthrough/${id}`,
    duration: 'PT5M', // ISO 8601 duration format (5 minutes)
  };

  return (
    <div className="demo-video-page">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(videoSchema)}
        </script>
      </Helmet>

      <Navigation 
        secondaryButtonText="Watch Demo"
        onSecondaryClick={() => navigate('/demo')}
        trackingLocation={`walkthrough_${id}_page`}
      />

      <div className="demo-video-container">
        <div className="demo-video-header">
          <h1>{walkthrough.title}</h1>
          <p className="demo-video-subtitle">
            {walkthrough.description}
          </p>
        </div>

        <div className="video-wrapper">
          <div className="demo-video">
            <video
              ref={videoRef}
              className="demo-video"
              playsInline
              controls
              preload="metadata"
              poster={walkthrough.poster}
              style={{ width: "100%", height: "auto", borderRadius: 12 }}
            />
            {error && (
              <p style={{ color: "tomato", marginTop: 8 }}>
                {error}
              </p>
            )}
          </div>
        </div>

        {walkthrough.transcript && walkthrough.transcript.length > 0 && (
          <div className="walkthrough-transcript">
            <h2>Transcript</h2>
            <div className="transcript-content">
              {walkthrough.transcript.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </div>
        )}
      </div>

      <Features />
    </div>
  );
}
